import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtUSD } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState("pending"); // pending | paid | expired | error
  const [data, setData] = useState(null);
  const { clear } = useCart();
  const polledRef = useRef(false);

  useEffect(() => {
    if (!session_id) return;

    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const r = await api.get(`/checkout/status/${session_id}`);
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          if (!polledRef.current) {
            polledRef.current = true;
            clear();
          }
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= maxAttempts) {
          setStatus("timeout");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
  }, [session_id, clear]);

  return (
    <div data-testid="checkout-success-page" className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
      {status === "pending" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-white/60" />
          <h1 className="mt-8 font-serif italic text-4xl">Confirming your order…</h1>
          <p className="mt-4 text-white/50">Don&apos;t refresh. This takes a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-12 h-12 mx-auto text-white" />
          <h1 className="mt-8 font-serif italic text-5xl">Thank you.</h1>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">
            Your order is confirmed. We&apos;ll DM tracking via TikTok within 24 hours.
            {data?.amount_total ? ` Total paid: ${fmtUSD(data.amount_total / 100)}.` : ""}
          </p>
          <div className="mt-10 flex items-center justify-center gap-4">
            <Link to="/" data-testid="success-home" className="px-8 py-4 bg-white text-black uppercase tracking-[0.22em] text-xs font-semibold">
              Back to home
            </Link>
            <a
              href="https://tiktok.com/@ayush.resellz"
              target="_blank"
              rel="noreferrer"
              data-testid="success-tiktok"
              className="px-8 py-4 border border-white/20 uppercase tracking-[0.22em] text-xs font-semibold"
            >
              DM on TikTok
            </a>
          </div>
        </>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <>
          <h1 className="mt-8 font-serif italic text-4xl">Something didn&apos;t land.</h1>
          <p className="mt-4 text-white/50">If you were charged, DM <a href="https://tiktok.com/@ayush.resellz" className="underline">@ayush.resellz</a>. Otherwise try again.</p>
          <Link to="/" className="mt-10 inline-block px-8 py-4 bg-white text-black uppercase tracking-[0.22em] text-xs font-semibold">
            Back to home
          </Link>
        </>
      )}
    </div>
  );
}
